// Generated by GitHub Copilot
import { motion, useReducedMotion } from "framer-motion";

/**
 * Render a premium tactile theme toggle with spring thumb motion and animated track feedback.
 *
 * @param {{
 *   checked: boolean,
 *   onToggle: () => void,
 *   label: string,
 *   hint?: string,
 *   disabled?: boolean,
 * }} props Theme toggle props.
 * @returns {JSX.Element} Animated theme toggle button.
 */
function ThemeToggle({ checked, onToggle, label, hint = "", disabled = false }) {
  const reduceMotion = useReducedMotion();
  const state = checked ? "night" : "day";

  const trackVariants = reduceMotion
    ? {
        day: { scaleX: 1, scaleY: 1 },
        night: { scaleX: 1, scaleY: 1 },
      }
    : {
        day: { scaleX: [1, 0.972, 1.016, 1], scaleY: [1, 0.99, 1] },
        night: { scaleX: [1, 0.97, 1.018, 1], scaleY: [1, 0.99, 1] },
      };

  const thumbVariants = {
    day: {
      x: 4,
      rotate: -8,
      scale: 1,
    },
    night: {
      x: 50,
      rotate: 9,
      scale: 1.03,
    },
  };

  const thumbTransition = reduceMotion
    ? { duration: 0 }
    : {
        type: "spring",
        stiffness: 500,
        damping: 22,
        mass: 0.74,
      };

  return (
    <motion.button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className="aurora-toggle"
      data-active={checked ? "true" : "false"}
      role="switch"
      aria-checked={checked}
      aria-label={label}
      title={hint}
      whileTap={reduceMotion ? undefined : { scale: 0.97 }}
    >
      <motion.span
        className="aurora-toggle__track"
        initial={false}
        animate={state}
        variants={trackVariants}
        transition={{
          duration: reduceMotion ? 0 : 0.44,
          times: reduceMotion ? undefined : [0, 0.4, 0.75, 1],
          ease: "easeOut",
        }}
      >
        <span className="aurora-toggle__sky-glow" aria-hidden="true" />

        <span className="aurora-toggle__rays" aria-hidden="true" />

        <span className="aurora-toggle__stars" aria-hidden="true">
          <span className="aurora-toggle__star aurora-toggle__star--1" />
          <span className="aurora-toggle__star aurora-toggle__star--2" />
          <span className="aurora-toggle__star aurora-toggle__star--3" />
          <span className="aurora-toggle__star aurora-toggle__star--4" />
        </span>

        <span className="aurora-toggle__cloud aurora-toggle__cloud--back" aria-hidden="true" />
        <span className="aurora-toggle__cloud aurora-toggle__cloud--front" aria-hidden="true" />

        <motion.span
          className="aurora-toggle__thumb"
          initial={false}
          animate={state}
          variants={thumbVariants}
          transition={thumbTransition}
        >
          <span className="aurora-toggle__thumb-glint" aria-hidden="true" />
          <span className="aurora-toggle__thumb-spots" aria-hidden="true">
            <span className="aurora-toggle__thumb-spot aurora-toggle__thumb-spot--1" />
            <span className="aurora-toggle__thumb-spot aurora-toggle__thumb-spot--2" />
            <span className="aurora-toggle__thumb-spot aurora-toggle__thumb-spot--3" />
          </span>
        </motion.span>
      </motion.span>
    </motion.button>
  );
}

export default ThemeToggle;